// Shared map of an overview: every selected corridor's centreline, the filtered
// deficiency sites and the multi-deficiency cluster rings. Same base maps, layer
// control, popups and focus protocol as the Safety Dashboard's map; no crash data.
// Receives ALREADY-FILTERED arrays. SVG renderer on purpose: Leaflet's canvas
// renderer in several panes lets only the top pane receive clicks.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import {
  MapContainer, TileLayer, LayersControl, LayerGroup, Marker, Pane, Polyline, CircleMarker, Popup, Tooltip, useMap, useMapEvents,
} from 'react-leaflet';
import DemoBasemap from '../Safety/DemoBasemap';
import StationsLayer, { DETAIL_ANALYSES, DETAIL_COLORS, GLR_BANDS, PATH_COLOR, SHAPES } from './StationsLayer';
import 'leaflet/dist/leaflet.css';
import { ANALYSIS_COLORS, ANALYSIS_NAMES, SEVERITY_COLORS, segmentName } from './overviewModel';
import { ManeuverButtons, ManeuverRows } from '../Safety/ManeuverLinks';
import CrashPopupBody, { CRASH_COLORS, crashPathOptions, crashRadius } from '../Safety/CrashPopup';
import { GUARDRAIL_LABEL, NET_CONDITIONS, NET_SOURCES } from '../SafetyOverview/rules';
import TopNSelect from '../Safety/TopNSelect';

const fmtMp = (v) => (v == null ? '—' : Number(v).toFixed(2));

// Road-network features (Safety Overview Part B): a letter per SOP condition, ringed in the
// colour of the source it was taken from
export const NET_SOURCE_COLORS = { ours: '#0d9488', dot: '#2563eb', osm: '#65a30d' };
const NET_GLYPH = { signal: 'S', majorIntersection: '+', busStop: 'B', crosswalk: 'C', school: 'Sc', emergency: 'H',
  roundabout: '○', railCrossing: 'RR', median: 'M' };
const NET_LABEL = { ...Object.fromEntries(NET_CONDITIONS.map(([k, label]) => [k, label])), guardrail: GUARDRAIL_LABEL };
const GUARDRAIL_COLOR = '#1e3a8a';          // existing guardrail beams: dark blue, on a light casing
const NET_SOURCE_NAME = Object.fromEntries(NET_SOURCES.map(([k, label]) => [k, label]));
const netIcons = {};
const netIcon = (f) => {
  const k = `${f.type}|${f.src}`;
  if (!netIcons[k]) {
    netIcons[k] = L.divIcon({ className: 'so-net-icon', iconSize: [20, 20], iconAnchor: [10, 10],
      html: `<span style="border-color:${NET_SOURCE_COLORS[f.src] || '#64748b'}">${NET_GLYPH[f.type] || '?'}</span>` });
  }
  return netIcons[k];
};

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => { if (bounds) map.fitBounds(bounds, { padding: [30, 30] }); }, [map, bounds]);
  return null;
}

function FlyToFocus({ focus, refs }) {
  const map = useMap();
  useEffect(() => {
    if (!focus || focus.lat == null) return undefined;
    const target = focus.id ? refs.current[focus.id] : null;
    const open = () => { try { if (target && target.openPopup) target.openPopup(); } catch (e) { /* layer gone */ } };
    map.flyTo([focus.lat, focus.lng], Math.max(map.getZoom(), focus.zoom || 15), { duration: 0.8 });
    map.once('moveend', open);
    return () => map.off('moveend', open);
  }, [map, focus, refs]);
  return null;
}

/** Double-click = add a Point of Interest (when the page keeps them). */
function DblClickCatcher({ onAdd }) {
  useMapEvents({ dblclick: (e) => { if (onAdd) onAdd({ lat: e.latlng.lat, lng: e.latlng.lng }); } });
  return null;
}
const PLACE_ICON = L.divIcon({ className: 'so-place-icon', html: '<span>★</span>', iconSize: [22, 22], iconAnchor: [11, 11] });

function PlacePopup({ place, index, onUpdate, onRemove }) {
  const [note, setNote] = useState(place.note || '');
  const [saved, setSaved] = useState(false);
  useEffect(() => { setNote(place.note || ''); }, [place.note]);
  useEffect(() => { if (!saved) return undefined; const t = setTimeout(() => setSaved(false), 2500); return () => clearTimeout(t); }, [saved]);
  const save = () => { onUpdate(place.id, note); setSaved(true); };
  return (
    <div className="safety-popup so-place-popup">
      <strong>Point of Interest {index + 1}</strong>
      <div className="safety-popup-mp">{place.lat.toFixed(5)}, {place.lng.toFixed(5)}{place.created ? ` · ${place.created.slice(0, 10)}` : ''}</div>
      <textarea aria-label="Note" rows={3} placeholder="Add a note" value={note}
        onChange={(e) => setNote(e.target.value)} onBlur={() => note !== (place.note || '') && save()} />
      <div className="safety-popup-actions">
        <button type="button" className="safety-btn" onClick={save}>Save note</button>
        <button type="button" className="safety-btn safety-btn-ghost" onClick={() => onRemove(place.id)}>Remove this Point of Interest</button>
        {saved && <span className="so-note-saved" role="status">✓ Note saved</span>}
      </div>
    </div>
  );
}

/** Rays of each maneuver of one intersection, switched on and off here (ISD view). */
function RaySwitches({ maneuvers, isd }) {
  const ids = (maneuvers || []).map((m) => m.id).filter(Boolean);
  if (!isd || !ids.length) return null;
  const hidden = new Set(isd.hidden);
  return (
    <div className="so-rays">
      <div className="so-rays-head">
        <strong>Sight rays on the map</strong>
        <button type="button" className="so-link" onClick={() => isd.setMany(ids, false)}>all</button>
        <button type="button" className="so-link" onClick={() => isd.setMany(ids, true)}>none</button>
      </div>
      {maneuvers.filter((m) => m.id).map((m) => (
        <div key={m.id} className="so-rays-row">
          <label>
            <input type="checkbox" checked={!hidden.has(m.id)} onChange={() => isd.toggle(m.id)} />
            <span>{m.name || m.id}{m.quality != null ? ` · ${Math.round(m.quality * 100)}%` : ''}</span>
          </label>
          <button type="button" className="so-link" title="Show the rays of this maneuver only"
            onClick={() => isd.only(ids, m.id)}>only</button>
        </div>
      ))}
    </div>
  );
}

/** Every crash of a crash cluster: the full list, and a ‹ › stepper through the crashes that
 *  shows each one's complete record (the same body as the crash's own popup). */
function ClusterCrashes({ members, analysis, recommendations }) {
  const [idx, setIdx] = useState(-1);
  const map = useMap();
  // the popup keeps its first size unless told: re-fit it (and let PopupSizer widen it) per step
  useEffect(() => { const p = map && map._popup; if (p) { p.options._widened = false; p.update(); map.fire('popupopen', { popup: p }); } }, [idx, map]);
  if (!members.length) return null;
  const step = (d) => setIdx((i) => (i < 0 ? (d > 0 ? 0 : members.length - 1) : (i + d + members.length) % members.length));
  const cur = idx >= 0 ? members[Math.min(idx, members.length - 1)] : null;
  return (
    <div className="so-cluster-crashes-box">
      <div className="so-cluster-step" role="group" aria-label="Crashes of this cluster">
        <button type="button" aria-label="Previous crash" onClick={() => step(-1)}>‹</button>
        <span>{cur ? `Crash ${idx + 1} of ${members.length}` : `${members.length} crashes · step through them`}</span>
        <button type="button" aria-label="Next crash" onClick={() => step(1)}>›</button>
        {cur && <button type="button" className="so-link" onClick={() => setIdx(-1)}>list</button>}
      </div>
      {cur ? (
        <div className="so-cluster-crash"><CrashPopupBody crash={cur} analysis={analysis} recommendations={recommendations} /></div>
      ) : (
        <ul className="so-cluster-crashes">
          {members.map((m, i) => (
            <li key={m.id}>
              <button type="button" className="so-link" onClick={() => setIdx(i)}>
                MP {fmtMp(m.milepost)} · {m.severity || m.severityClass} · {m.manner || 'crash'} · {m.datetime ? m.datetime.slice(0, 10) : ''}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// rank badges of the map's Top N (one icon per rank, built once)
const rankIcons = {};
const rankIcon = (rank, kind, pinned) => {
  const k = `${kind}|${rank}|${pinned ? 1 : 0}`;
  if (!rankIcons[k]) {
    rankIcons[k] = L.divIcon({ className: `so-rank-badge so-rank-${kind}`, iconSize: null, iconAnchor: [-6, 22],
      html: `<span>${pinned ? '📌' : ''}#${rank}</span>` });
  }
  return rankIcons[k];
};
const TOP_OPEN_KEY = 'safetyOverview:topOnMapOpen';

/** Top on map (Safety Overview): how many of the top-ranked sites and clusters the map shows, and a
 *  Prev / Next through them (pinned rows too); the crash-cluster stepper lives here as well. The
 *  ranks are the tables' # and the insights' order, so the map's head is the panels' head. */
function TopOnMap({ nav, crash, onGo }) {
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(TOP_OPEN_KEY) !== '0'; } catch (e) { return true; } });
  const [idx, setIdx] = useState({ sites: -1, clusters: -1 });
  const toggle = (v) => { setOpen(v); try { localStorage.setItem(TOP_OPEN_KEY, v ? '1' : '0'); } catch (e) { /* private window */ } };
  useEffect(() => { setIdx({ sites: -1, clusters: -1 }); }, [nav.sites.list.length, nav.clusters.list.length, nav.sites.value, nav.clusters.value]);
  const step = (kind, d) => {
    const list = nav[kind].list.filter((x) => x.lat != null);
    if (!list.length) return;
    const cur = idx[kind];
    const i = cur < 0 ? (d > 0 ? 0 : list.length - 1) : (cur + d + list.length) % list.length;
    setIdx((x) => ({ ...x, [kind]: i }));
    onGo(list[i], kind === 'clusters' ? 15 : 16);
  };
  if (!open) {
    return <button type="button" className="so-top-chip" onClick={() => toggle(true)} title="Top sites and clusters on the map">Top on map ▾</button>;
  }
  const row = (kind, label) => {
    const n = nav[kind];
    const list = n.list.filter((x) => x.lat != null);
    const cur = idx[kind] >= 0 ? list[idx[kind]] : null;
    return (
      <div className="so-top-row" title={n.title}>
        <span className="so-top-label">{label}</span>
        <TopNSelect value={n.value} onChange={n.onChange} max={n.max} className="" />
        <button type="button" aria-label={`Previous ${label.toLowerCase()}`} onClick={() => step(kind, -1)}>‹</button>
        <span className="so-top-count">{cur ? `#${n.rank.get(cur.id) ?? '—'}${n.pinned.includes(cur.id) ? ' 📌' : ''} · ${idx[kind] + 1} of ${list.length}` : `${list.length} on map`}</span>
        <button type="button" aria-label={`Next ${label.toLowerCase()}`} onClick={() => step(kind, 1)}>›</button>
      </div>
    );
  };
  return (
    <div className="so-top-panel" role="group" aria-label="Top on map">
      <div className="so-top-head">
        <span>Top on map</span>
        <button type="button" className="lg-close" onClick={() => toggle(false)} title="Minimize">–</button>
      </div>
      {row('sites', 'Sites')}
      {row('clusters', 'Clusters')}
      {crash.list.length > 0 && (
        <div className="so-top-row">
          <span className="so-top-label">Crash clusters</span>
          <span />
          <button type="button" aria-label="Previous crash cluster" onClick={() => crash.go(-1)}>‹</button>
          <span className="so-top-count">{crash.idx < 0 ? `${crash.list.length} places` : `${crash.idx + 1} of ${crash.list.length}`}</span>
          <button type="button" aria-label="Next crash cluster" onClick={() => crash.go(1)}>›</button>
        </div>
      )}
    </div>
  );
}

/** A popup taller than 75% of the map gets wider (and, when still too tall, scrolls). */
function PopupSizer() {
  const map = useMapEvents({
    popupopen: (e) => {
      const popup = e.popup;
      const fit = () => {
        const el = popup.getElement();
        if (!el) return;
        const size = map.getSize();
        if (el.offsetHeight > 0.75 * size.y && !popup.options._widened) {
          popup.options._widened = true;
          popup.options.maxWidth = Math.min(size.x * 0.85, 760);
          popup.options.minWidth = Math.min(size.x * 0.55, 520);
          popup.options.maxHeight = Math.round(size.y * 0.85);
          popup.update();
        }
      };
      setTimeout(fit, 0);
    },
    popupclose: (e) => { if (e.popup && e.popup.options) delete e.popup.options._widened; },
  });
  return null;
}

// the layer-control names of the base maps -> the keys the PDF export understands
const BASE_LAYER_KEYS = { 'Street (OSM)': 'street', 'Satellite (Esri)': 'satellite', 'Terrain (OpenTopoMap)': 'terrain', 'Street map': 'plain' };

/** Reports the base map the user picked in the layer control (the PDF draws the same one). */
function BaseLayerWatcher({ onChange }) {
  useMapEvents({ baselayerchange: (e) => { if (onChange) onChange(BASE_LAYER_KEYS[e.name] || 'street'); } });
  return null;
}

function ZoomWatcher({ onZoom }) {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  useEffect(() => { onZoom(map.getZoom()); }, [map, onZoom]);
  return null;
}

/** A legend swatch drawn hollow: the deficient stations too short to count. */
const HollowSwatch = ({ shape = 'dot', label }) => (
  <div className="lg-row"><i className={`${SHAPE_CLASS[shape] || 'lg-dot'} ov-lg-hollow`} style={{ borderColor: DETAIL_COLORS.bad }} />{label}</div>
);

const SHAPE_CLASS = { dot: 'lg-dot', circle: 'lg-dot', square: 'lg-dot ov-lg-square', diamond: 'lg-dot ov-lg-diamond' };
const Swatch = ({ color, label, shape = 'line' }) => (
  <div className="lg-row"><i className={SHAPE_CLASS[shape] || 'lg-line'} style={{ background: color }} />{label}</div>
);

/** What the single-analysis view draws, in that analysis' own report colours. */
export const ISD_CASES = [
  ['B1', 'left turn from the minor road'], ['B2', 'right turn from the minor road'],
  ['B3', 'crossing from the minor road'], ['F', 'left turn from the major road'],
];

function DetailLegend({ analysis, isd, grwShield = false }) {
  const shape = SHAPES[analysis] || 'dot';
  if (analysis === 'SSD' || analysis === 'PSD') {
    return (<><Swatch shape={shape} color={DETAIL_COLORS.bad} label={analysis === 'SSD' ? 'Short of stopping sight distance, in a deficiency run' : 'Unsafe pass, in a deficiency run'} />
      <HollowSwatch shape={shape} label="Too short to count (below the minimum run)" />
      <Swatch shape={shape} color={DETAIL_COLORS.ok} label="Station passes" /></>);
  }
  if (analysis === 'GRW') {
    return (<><Swatch shape={shape} color={DETAIL_COLORS.bad} label={grwShield ? 'Warranted, no guardrail (deficiency)' : 'Guardrail warranted (YES)'} />
      <HollowSwatch shape={shape} label="Warranted, too short to count (below the minimum run)" />
      {grwShield && <Swatch shape={shape} color={DETAIL_COLORS.shielded} label="Warranted, existing guardrail (shielded)" />}
      <Swatch shape={shape} color={DETAIL_COLORS.ok} label="Not warranted (NO)" />
      {grwShield && <div className="lg-row"><i className="lg-line" style={{ background: GUARDRAIL_COLOR }} />Existing guardrail</div>}</>);
  }
  if (analysis === 'GLR') return <>{GLR_BANDS.map(([label, color]) => <Swatch key={label} shape="dot" color={color} label={`${label} glare / yr`} />)}</>;
  if (analysis === 'FSA') {
    return (<><div className="lg-row"><i className="ov-lg-tri" style={{ borderBottomColor: DETAIL_COLORS.flat }} />Flat spot, pointing downhill</div>
      <div className="lg-row"><i className="ov-lg-tri" style={{ borderBottomColor: DETAIL_COLORS.slope }} />Station gradient direction</div></>);
  }
  if (analysis === 'ISD') {
    return (<><Swatch color={DETAIL_COLORS.bad} label="Sight ray: target not visible" />
      <Swatch color={DETAIL_COLORS.ok} label="Sight ray: target visible" />
      {isd && isd.hidden.length > 0 && (
        <div className="lg-row">
          <button type="button" className="so-link" onClick={isd.reset}>Show every ray ({isd.hidden.length} maneuver{isd.hidden.length === 1 ? '' : 's'} hidden)</button>
        </div>
      )}</>);
  }
  return Object.entries(SEVERITY_COLORS).map(([s, c]) => <Swatch key={s} color={c} label={`Curve, ${s}`} />);
}

function Legend({ colourBy, analyses, roads, details, showIntersections, showPaths, showCrashes, isd, showNetwork, grwShield = false }) {
  const detail = details.length > 0;
  const [open, setOpen] = React.useState(true);
  if (!open) {
    return <button type="button" className="safety-map-legend safety-legend-min" onClick={() => setOpen(true)}>Legend</button>;
  }
  return (
    <div className="safety-map-legend">
      <div className="lg-head">
        <span className="lg-title lg-title-first">{detail ? `${details[0]} · ${ANALYSIS_NAMES[details[0]]}` : colourBy === 'severity' ? 'Sites by severity' : 'Sites by analysis'}</span>
        <button type="button" className="lg-close" onClick={() => setOpen(false)} title="Minimize legend">–</button>
      </div>
      {detail ? (
        <>
          {details.map((a, i) => (
            <React.Fragment key={a}>
              {i > 0 && <div className="lg-title">{a} · {ANALYSIS_NAMES[a]}</div>}
              <DetailLegend analysis={a} isd={isd} grwShield={grwShield} />
            </React.Fragment>
          ))}
          {details.some((a) => DETAIL_ANALYSES.includes(a)) && (
            <>
              <div className="lg-title">Deficiency runs (halo, follows the severity filter)</div>
              {Object.entries(SEVERITY_COLORS).map(([s, c]) => (
                <div className="lg-row" key={s}><i className="lg-line ov-lg-halo" style={{ background: c }} />{s}</div>))}
            </>
          )}
          {showPaths && <Swatch color={PATH_COLOR} label="Analysis path" />}
        </>
      ) : colourBy === 'severity'
        ? Object.entries(SEVERITY_COLORS).map(([s, c]) => (
          <div className="lg-row" key={s}><i className="lg-line" style={{ background: c }} />{s}</div>))
        : analyses.map((a) => (
          <div className="lg-row" key={a} title={ANALYSIS_NAMES[a]}><i className="lg-line" style={{ background: ANALYSIS_COLORS[a] }} />{a}</div>))}
      {analyses.length > 1 && <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: '#f8fafc', borderStyle: 'dashed' }} />Multi-deficiency cluster</div>}
      {showIntersections && <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: ANALYSIS_COLORS.ISD }} />Intersection</div>}
      {showCrashes && (
        <>
          <div className="lg-title">Crashes</div>
          <div className="lg-row"><i className="lg-dot" style={{ background: CRASH_COLORS.fatal }} />Fatal</div>
          <div className="lg-row"><i className="lg-dot" style={{ background: CRASH_COLORS.injury }} />Injury</div>
          <div className="lg-row"><i className="lg-dot" style={{ background: CRASH_COLORS.pdo }} />Property damage</div>
          <div className="lg-row"><i className="lg-dot lg-hollow" />Animal strike</div>
          <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: '#e8a30c' }} />Glare-time match</div>
          <div className="lg-row"><i className="lg-dot lg-ring" style={{ borderColor: '#6b2e8a' }} />Crash cluster</div>
        </>
      )}
      {showNetwork && (
        <>
          <div className="lg-title">Road network (SOP), taken from</div>
          {NET_SOURCES.map(([k, label]) => (
            <div className="lg-row" key={k}><i className="lg-dot lg-ring" style={{ borderColor: NET_SOURCE_COLORS[k] }} />{label}</div>
          ))}
          <div className="lg-row ov-legend-mp" title="Road features the passing-zone rules use">
            {NET_CONDITIONS.map(([k, label]) => `${NET_GLYPH[k]} ${label.split(' ')[0].toLowerCase()}`).join(' · ')}
          </div>
        </>
      )}
      <div className="lg-title">Roads</div>
      {roads.map((r) => (
        <div className="lg-row" key={r.road}>
          <i className="lg-line" style={{ background: detail ? PATH_COLOR : r.color }} />
          {r.road}<span className="ov-legend-mp"> · {r.ranges.map(([a, b]) => `MP ${a.toFixed(1)}–${b.toFixed(1)}`).join(' · ')}</span>
        </div>
      ))}
    </div>
  );
}

const ReportButton = ({ url, label }) => (url ? (
  <a className="safety-btn safety-report-btn" href={url} target="_blank" rel="noreferrer">{label} ↗</a>
) : null);

export default function OverviewMap({ segments, sites, clusters, intersections = [], analyses, colourBy, roadColors, focus,
  details = [], stationLayers = null, showPaths = true, direction = '',
  crashes = null, crashClusters = [], crashAnalysis = 'ALL', recommendations = [], isd = null,
  places = null, onAddPlace = null, onUpdatePlace = null, onRemovePlace = null, network = null, onBaseLayer = null,
  canDeleteNetwork = null, onDeleteNetwork = null, grwShield = false, topNav = null }) {
  // topNav (Safety Overview): the Top on map control — {sites, clusters: {value, onChange, max, rank, list, pinned, title}}
  // grwShield: existing guardrails shield GRW warrants (the page's rule) — the GRW legend says so
  // canDeleteNetwork(src) -> may this user delete a road element of that source; onDeleteNetwork(feature) asks to
  // network: the road-network features the PSD rules used ({type, mp, lat, lng, label, src, segName})
  // places: Points of Interest (double-click the map to add one); null = the feature is off
  // isd: {cases, hidden, onToggle} — the maneuver cases present in the ISD rays and the ones switched off
  // crashes: null = no crash data (the Project Overview); an array = the Safety Overview's
  // filtered crashes ({...crash, id: 'crash:<segKey>:<id>', lng}) and crashClusters their clusters.
  // details: the selected analyses when there are at most three (else []). Their stations are
  // drawn in each report's own colours on a light purple path; those analyses' deficiency runs
  // stay clickable but invisible, except HCA whose runs ARE the curves (severity colours).
  // Intersections are an ISD subject: shown only while ISD is among the selected analyses.
  const detail = details.length > 0;
  // every shown segment of a demo state -> that state's fictional basemap
  const basemap = segments.length && segments.every((s) => s.basemap && s.basemap === segments[0].basemap) ? segments[0].basemap : null;
  const withStations = new Set(details.filter((a) => DETAIL_ANALYSES.includes(a)));
  const showIntersections = analyses.includes('ISD');
  const refs = useRef({});
  const [zoom, setZoom] = useState(null);
  // crash-cluster navigator: step through the clusters on the map (their popups carry the data)
  const [clusterIdx, setClusterIdx] = useState(-1);
  const [navFocus, setNavFocus] = useState(null);
  const navClusters = useMemo(() => (crashes ? crashClusters.filter((c) => c.lat != null) : []), [crashes, crashClusters]);
  useEffect(() => { setClusterIdx(-1); }, [navClusters.length]);
  const [placeIdx, setPlaceIdx] = useState(-1);
  const goPlace = (step) => {
    if (!places || !places.length) return;
    const i = placeIdx < 0 ? (step > 0 ? 0 : places.length - 1) : (placeIdx + step + places.length) % places.length;
    setPlaceIdx(i);
    const p = places[i];
    setNavFocus({ id: p.id, lat: p.lat, lng: p.lng, zoom: 16, stamp: Date.now() });
  };
  useEffect(() => { if (places && placeIdx >= places.length) setPlaceIdx(places.length - 1); }, [places, placeIdx]);
  const goCluster = (step) => {
    if (!navClusters.length) return;
    const i = clusterIdx < 0 ? (step > 0 ? 0 : navClusters.length - 1) : (clusterIdx + step + navClusters.length) % navClusters.length;
    setClusterIdx(i);
    const c = navClusters[i];
    setNavFocus({ id: c.id, lat: c.lat, lng: c.lng, zoom: 15, stamp: Date.now() });
  };
  const shownFocus = navFocus && (!focus || navFocus.stamp > (focus.stamp || 0)) ? navFocus : focus;
  // Cluster rings are a close-up aid: they show only from zoom 13, where the colours under
  // them read clearly. Before the first zoom reading nothing is drawn (the first view of an
  // order is the whole order).
  const far = zoom == null || zoom < 13;
  const mid = zoom != null && zoom < 15;
  const stationsShown = withStations.size > 0;
  const runColourBy = colourBy;
  const colorOfRun = (s) => (detail && s.analysis === 'HCA' ? SEVERITY_COLORS[s.severity] : null);
  const bounds = useMemo(() => {
    const pts = segments.flatMap((s) => Object.values(s.directions || {}))
      .flatMap((d) => d.points.filter((p) => p[0] != null).map((p) => [p[0], p[1]]));
    return pts.length ? pts : null;
  }, [segments]);
  const segByKey = useMemo(() => new Map(segments.map((s) => [s.key, s])), [segments]);
  const roads = useMemo(() => {
    const m = new Map();
    segments.forEach((s) => {
      if (!m.has(s.road)) m.set(s.road, { road: s.road, color: roadColors[s.road], ranges: [] });
      if (s.mpMin != null) m.get(s.road).ranges.push([s.mpMin, s.mpMax]);
    });
    return Array.from(m.values()).map((r) => ({ ...r, ranges: r.ranges.sort((a, b) => a[0] - b[0]) }));
  }, [segments, roadColors]);
  // a road-network element's popup: what, where, from which source, what it does — and Delete for those who may
  const netPopup = (f) => (
    <div className="safety-popup">
      <strong>{NET_LABEL[f.type]}</strong>
      <div className="safety-popup-mp">{f.segName} · MP {fmtMp(f.mp)}{f.mpEnd != null ? `–${fmtMp(f.mpEnd)}` : ''}</div>
      <table><tbody>
        <tr><td>What</td><td>{f.label}</td></tr>
        <tr><td>Taken from</td><td><b style={{ color: NET_SOURCE_COLORS[f.src] }}>{NET_SOURCE_NAME[f.src]}</b>{f.srcLabel ? ` · ${f.srcLabel}` : ''}</td></tr>
        {f.ref ? <tr><td>Reference</td><td>{f.ref}</td></tr> : null}
        {f.type === 'guardrail'
          ? <tr><td>Effect</td><td>shields the guardrail warrant on the {f.side === 'P' ? 'positive-direction' : f.side === 'N' ? 'negative-direction' : 'both'} side{f.side === 'both' ? 's' : ''}</td></tr>
          : <tr><td>SOP rule</td><td>no passing zone {f.approachFt} ft approaching / {f.departFt} ft departing</td></tr>}
      </tbody></table>
      {f.src === 'osm' && <div className="safety-muted">© OpenStreetMap contributors</div>}
      {onDeleteNetwork && canDeleteNetwork && canDeleteNetwork(f.src) && (
        <div className="safety-popup-actions">
          <button type="button" className="safety-btn so-danger" onClick={() => onDeleteNetwork(f)}>Delete this element…</button>
        </div>
      )}
    </div>
  );
  const colorOf = (s) => colorOfRun(s) || (runColourBy === 'severity' ? SEVERITY_COLORS[s.severity] : ANALYSIS_COLORS[s.analysis]) || '#c92a2a';

  if (!bounds) return <div className="safety-map-empty">No surveyed centreline to show for this selection.</div>;

  return (
    <div className="safety-map-holder">
      <MapContainer bounds={bounds} className="safety-map" scrollWheelZoom doubleClickZoom={!onAddPlace}>
        <DblClickCatcher onAdd={onAddPlace} />
        <FitBounds bounds={bounds} />
        <ZoomWatcher onZoom={setZoom} />
        <BaseLayerWatcher onChange={onBaseLayer} />
        <PopupSizer />
        <FlyToFocus focus={shownFocus} refs={refs} />
        <Pane name="ovSites" style={{ zIndex: 410 }} />
        <Pane name="ovIntersections" style={{ zIndex: 420 }} />
        <Pane name="ovRails" style={{ zIndex: 418 }} />
        <Pane name="ovClusters" style={{ zIndex: 430 }} />
        <Pane name="ovCrashClusters" style={{ zIndex: 425 }} />
        <Pane name="ovCrashes" style={{ zIndex: 460 }} />
        <Pane name="ovDecor" style={{ zIndex: 470 }} />
        {shownFocus && shownFocus.lat != null && (
          <CircleMarker key={shownFocus.stamp} center={[shownFocus.lat, shownFocus.lng]} radius={16} pane="ovDecor" interactive={false}
            pathOptions={{ color: '#ff9433', weight: 3, fillOpacity: 0.08, dashArray: '2 4' }} />
        )}
        <LayersControl position="topright">
          {/* a demo state brings its own fictional map; real layers would show the wrong place */}
          {basemap ? (
            <LayersControl.BaseLayer checked name="Street map">
              <DemoBasemap state={basemap} />
            </LayersControl.BaseLayer>
          ) : (
            <>
          <LayersControl.BaseLayer checked name="Street (OSM)">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite (Esri)">
            <TileLayer attribution="Imagery &copy; Esri, Maxar, Earthstar Geographics"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" maxZoom={19} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Terrain (OpenTopoMap)">
            <TileLayer attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" maxZoom={17} />
          </LayersControl.BaseLayer>
            </>
          )}

          {showPaths && (
          <LayersControl.Overlay checked name="Analysis paths">
            <LayerGroup>
              {segments.flatMap((s) => Object.entries(s.directions || {}).filter(([dir]) => !direction || dir === direction).map(([dir, d]) => (
                <Polyline key={`${s.key}-${dir}`} positions={d.points.filter((p) => p[0] != null).map((p) => [p[0], p[1]])}
                  pathOptions={{ color: detail ? PATH_COLOR : (roadColors[s.road] || '#7aa7e8'), weight: detail ? 4 : 3, opacity: detail ? 0.85 : 0.6 }}>
                  <Tooltip sticky>{segmentName(s)} · {dir === 'P' ? 'positive' : 'negative'} direction{s.projectId ? ` · project #${s.projectId}` : ''}</Tooltip>
                </Polyline>
              )))}
            </LayerGroup>
          </LayersControl.Overlay>
          )}

          {stationsShown && (
            <LayersControl.Overlay checked name={`${Array.from(withStations).join(' · ')} stations`}>
              <StationsLayer layers={stationLayers || []} />
            </LayersControl.Overlay>
          )}

          <LayersControl.Overlay checked name="Deficiency sites">
            <LayerGroup>
              {sites.map((s) => {
                const seg = segByKey.get(s.segKey);
                const popup = (
                  <Popup>
                    <div className="safety-popup">
                      <strong>{s.analysis} · {ANALYSIS_NAMES[s.analysis]}</strong>
                      <div className="safety-popup-mp">
                        {seg ? segmentName(seg) : s.road} · MP {fmtMp(s.mpStart)}{s.lengthMi ? `–${fmtMp(s.mpEnd)} · ${s.lengthMi.toFixed(2)} mi` : ''}
                      </div>
                      <table><tbody>
                        <tr><td>Direction</td><td>{s.direction === 'P' ? 'Positive' : 'Negative'}</td></tr>
                        <tr><td>Severity</td><td>{s.severity}</td></tr>
                        <tr><td>Worst</td><td>{s.worstText || '—'}</td></tr>
                        {s.projectId ? <tr><td>Project</td><td>#{s.projectId}</td></tr> : null}
                        <ManeuverRows maneuvers={s.maneuvers} />
                      </tbody></table>
                      <div className="safety-popup-actions">
                        {/* ISD: one page per maneuver — link every maneuver that is short, worst first */}
                        <ManeuverButtons maneuvers={s.maneuvers} fallbackUrl={s.reportUrl}
                          fallbackLabel={`Open ${s.analysis} report at MP ${fmtMp(s.mpStart)}`} />
                      </div>
                    </div>
                  </Popup>
                );
                const tip = <Tooltip sticky>{s.analysis} · {seg ? segmentName(seg) : s.road} · MP {fmtMp(s.mpStart)} · {s.severity}</Tooltip>;
                const keep = (el) => { if (el) refs.current[s.id] = el; else delete refs.current[s.id]; };
                return s.coords && s.coords.length > 1 ? (
                  <Polyline key={s.id} ref={keep} positions={s.coords} pane="ovSites"
                    pathOptions={withStations.has(s.analysis)
                      // a HALO under the station marks, in the run's severity colour: it shows where the
                      // (severity-filtered) deficiency runs are and is the click target for their popup
                      ? { color: SEVERITY_COLORS[s.severity] || '#c92a2a', weight: far ? 7 : 13, opacity: 0.38 }
                      : { color: colorOf(s), weight: far ? 3 : 5, opacity: 0.95 }}>{tip}{popup}</Polyline>
                ) : (s.lat != null ? (
                  <CircleMarker key={s.id} ref={keep} center={[s.lat, s.lng]} radius={6} pane="ovSites"
                    pathOptions={{ color: '#ffffff', weight: 1.5, fillColor: colorOf(s), fillOpacity: 0.95 }}>{tip}{popup}</CircleMarker>
                ) : null);
              })}
            </LayerGroup>
          </LayersControl.Overlay>

          {showIntersections && intersections.length > 0 && (
            <LayersControl.Overlay checked name="Intersections (ISD)">
              <LayerGroup>
                {intersections.filter((ix) => ix.lat != null).map((ix) => (
                  <CircleMarker key={ix.id} center={[ix.lat, ix.lng]} radius={5} pane="ovIntersections"
                    ref={(el) => { if (el) refs.current[ix.id] = el; else delete refs.current[ix.id]; }}
                    pathOptions={ix.severity
                      ? { color: '#ffffff', weight: 1.5, fillColor: SEVERITY_COLORS[ix.severity], fillOpacity: 0.95 }
                      : { color: ANALYSIS_COLORS.ISD, weight: 2, fillColor: '#0b1222', fillOpacity: 0.9 }}>
                    <Tooltip>Intersection {ix.name || ''} · MP {fmtMp(ix.mp)}{ix.severity ? ` · sight distance short (${ix.severity})` : ' · sight distance met'}</Tooltip>
                    <Popup>
                      <div className="safety-popup">
                        <strong>Intersection {ix.name || ''}</strong>
                        <div className="safety-popup-mp">{ix.segName} · MP {fmtMp(ix.mp)}</div>
                        <table><tbody>
                          {ix.isdFt ? <tr><td>Required sight distance</td><td>{Math.round(ix.isdFt)} ft</td></tr> : null}
                          {ix.quality != null ? <tr><td>Worst quality</td><td>{(ix.quality * 100).toFixed(0)}%</td></tr> : null}
                          <tr><td>Result</td><td>{ix.severity ? `short of required · ${ix.severity}` : 'meets the required distance'}</td></tr>
                          <ManeuverRows maneuvers={ix.maneuvers} />
                        </tbody></table>
                        {withStations.has('ISD') && <RaySwitches maneuvers={ix.maneuvers} isd={isd} />}
                        <div className="safety-popup-actions">
                          <ManeuverButtons maneuvers={ix.maneuvers} fallbackUrl={ix.reportUrl}
                            fallbackLabel={`Open ISD report at MP ${fmtMp(ix.mp)}`} />
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {topNav && (topNav.sites.value > 0 || topNav.clusters.value > 0) && (
            <LayersControl.Overlay checked name="Top ranks">
              <LayerGroup>
                {topNav.sites.value > 0 && sites.filter((s) => s.lat != null).map((s) => (
                  <Marker key={`rk-${s.id}`} position={[s.lat, s.lng]} pane="ovDecor" interactive={false}
                    icon={rankIcon(topNav.sites.rank.get(s.id) ?? '—', 'site', topNav.sites.pinned.includes(s.id))} />
                ))}
                {topNav.clusters.value > 0 && clusters.filter((c) => c.lat != null).map((c) => (
                  <Marker key={`rk-${c.id}`} position={[c.lat, c.lng]} pane="ovDecor" interactive={false}
                    icon={rankIcon(topNav.clusters.rank.get(c.id) ?? '—', 'cluster', topNav.clusters.pinned.includes(c.id))} />
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          <LayersControl.Overlay checked name="Multi-deficiency clusters">
            <LayerGroup>
              {/* Rings are a close-up aid: zoomed out over a whole order they would bury the roads in white. */}
              {(far ? [] : clusters.filter((c) => c.lat != null)).map((c) => (
                <CircleMarker key={c.id} center={[c.lat, c.lng]} radius={mid ? 8 : 12} pane="ovClusters"
                  ref={(el) => { if (el) refs.current[c.id] = el; else delete refs.current[c.id]; }}
                  pathOptions={{ color: '#f8fafc', weight: mid ? 1.4 : 2, fillOpacity: 0, dashArray: '4 3' }}>
                  <Tooltip>{c.n} analyses · {segmentName(c)} · MP {fmtMp(c.mpStart)}</Tooltip>
                  <Popup>
                    <div className="safety-popup">
                      <strong>{c.n} analyses at one place</strong>
                      <div className="safety-popup-mp">{segmentName(c)} · MP {fmtMp(c.mpStart)}–{fmtMp(c.mpEnd)}</div>
                      <table><tbody>
                        <tr><td>Analyses</td><td>{c.analyses.join(' · ')}</td></tr>
                        <tr><td>Sites</td><td>{c.sites}</td></tr>
                        <tr><td>Worst severity</td><td>{c.severity}</td></tr>
                      </tbody></table>
                      <div className="safety-popup-actions">
                        {Object.entries(c.reports).map(([a, site]) => (
                          <ReportButton key={a} url={site.reportUrl} label={`Open ${a} report at MP ${fmtMp(site.mpStart)}`} />
                        ))}
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </LayerGroup>
          </LayersControl.Overlay>

          {network && network.length > 0 && (
            <LayersControl.Overlay checked name="Road network (SOP)">
              <LayerGroup>
                {/* a close-up aid like the cluster rings: crosswalks alone would bury a whole order */}
                {/* existing guardrails: a steel line along the road on their side of travel */}
                {network.filter((f) => f.type === 'guardrail' && (f.coords || []).length > 1).map((f) => (
                  <React.Fragment key={`${f.id}-line`}>
                  <Polyline positions={f.coords} pane="ovRails" interactive={false}
                    pathOptions={{ color: '#ffffff', weight: far ? 5 : 8, opacity: 0.85, lineCap: 'butt' }} />
                  <Polyline positions={f.coords} pane="ovRails"
                    pathOptions={{ color: GUARDRAIL_COLOR, weight: far ? 3 : 5, opacity: 1, lineCap: 'butt' }}>
                    <Tooltip sticky>{GUARDRAIL_LABEL} · {f.label} · {NET_SOURCE_NAME[f.src]}{f.srcLabel ? ` (${f.srcLabel})` : ''}
                      {f.side === 'P' ? ' · positive-direction side' : f.side === 'N' ? ' · negative-direction side' : ' · centre'}</Tooltip>
                    <Popup>{netPopup(f)}</Popup>
                  </Polyline>
                  </React.Fragment>
                ))}
                {(far ? [] : network.filter((f) => f.lat != null && f.type !== 'guardrail')).map((f) => (
                  <Marker key={f.id} position={[f.lat, f.lng]} icon={netIcon(f)}
                    ref={(el) => { if (el) refs.current[f.id] = el; else delete refs.current[f.id]; }}>
                    <Tooltip>{NET_LABEL[f.type]} · {NET_SOURCE_NAME[f.src]}</Tooltip>
                    <Popup>{netPopup(f)}</Popup>
                  </Marker>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}

          {crashes && (
            <LayersControl.Overlay checked name="Crashes">
              <LayerGroup>
                {crashes.filter((c) => c.lat != null).map((c) => (
                  <React.Fragment key={c.id}>
                    {c.glareMatch && (
                      <CircleMarker center={[c.lat, c.lng]} radius={10} pane="ovCrashes" interactive={false}
                        pathOptions={{ color: '#e8a30c', weight: 2.5, fillOpacity: 0 }} />
                    )}
                    <CircleMarker center={[c.lat, c.lng]} pane="ovCrashes" radius={crashRadius(c)} pathOptions={crashPathOptions(c)}
                      ref={(el) => { if (el) refs.current[c.id] = el; else delete refs.current[c.id]; }}>
                      <Popup><CrashPopupBody crash={c} analysis={crashAnalysis} recommendations={recommendations} /></Popup>
                    </CircleMarker>
                  </React.Fragment>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}
          {crashes && (
            <LayersControl.Overlay checked name="Crash clusters">
              <LayerGroup>
                {crashClusters.filter((cl) => cl.lat != null).map((cl) => (
                  <CircleMarker key={cl.id} center={[cl.lat, cl.lng]} radius={mid ? 12 : 18} pane="ovCrashClusters"
                    ref={(el) => { if (el) refs.current[cl.id] = el; else delete refs.current[cl.id]; }}
                    pathOptions={{ color: '#6b2e8a', weight: 2.5, fillColor: '#6b2e8a', fillOpacity: 0.07, dashArray: '4 3' }}>
                    <Tooltip>{cl.count} crashes · {cl.road} · MP {fmtMp(cl.mpStart)}{cl.mpEnd > cl.mpStart ? `–${fmtMp(cl.mpEnd)}` : ''}</Tooltip>
                    <Popup>
                      <div className="safety-popup">
                        <strong>Crash cluster · {cl.count} crashes</strong>
                        <div className="safety-popup-mp">{cl.road} · MP {fmtMp(cl.mpStart)}{cl.mpEnd > cl.mpStart ? `–${fmtMp(cl.mpEnd)}` : ''}{cl.towns ? ` · ${cl.towns}` : ''}</div>
                        <table><tbody>
                          <tr><td>Fatal · injury · property damage</td><td>{cl.mix ? `${cl.mix.fatal} · ${cl.mix.injury} · ${cl.mix.pdo}` : '—'}</td></tr>
                          <tr><td>Leading type</td><td>{cl.leading || '—'}</td></tr>
                          {cl.years ? <tr><td>Years</td><td>{cl.years}</td></tr> : null}
                          <tr><td>Deficiency sites near</td><td>{cl.hit ? cl.hitAnalyses.join(' · ') : 'none: the analyses miss this cluster'}</td></tr>
                        </tbody></table>
                        <ClusterCrashes members={cl.members || []} analysis={crashAnalysis} recommendations={recommendations} />
                        {(cl.nearSites || []).length > 0 && (
                          <div className="safety-popup-actions">
                            {cl.nearSites.map((s) => (
                              <ManeuverButtons key={s.id} maneuvers={s.maneuvers} fallbackUrl={s.reportUrl}
                                fallbackLabel={`Open ${s.analysis} report at MP ${fmtMp(s.mpStart)}`} />
                            ))}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </LayerGroup>
            </LayersControl.Overlay>
          )}
        </LayersControl>
        {(places || []).map((p, i) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={PLACE_ICON} zIndexOffset={1000}
            ref={(el) => { if (el) refs.current[p.id] = el; else delete refs.current[p.id]; }}>
            <Tooltip>{`Point of Interest ${i + 1}${p.note ? ` · ${p.note.slice(0, 60)}` : ''}`}</Tooltip>
            <Popup><PlacePopup place={p} index={i} onUpdate={onUpdatePlace} onRemove={onRemovePlace} /></Popup>
          </Marker>
        ))}
      </MapContainer>
      {places && places.length > 0 && (
        <div className="so-place-nav" role="group" aria-label="Points of Interest">
          <button type="button" aria-label="Previous Point of Interest" onClick={() => goPlace(-1)}>‹</button>
          <span>★ {placeIdx < 0 ? `${places.length} Point${places.length === 1 ? '' : 's'} of Interest` : `Point of Interest ${placeIdx + 1} of ${places.length}`}</span>
          <button type="button" aria-label="Next Point of Interest" onClick={() => goPlace(1)}>›</button>
        </div>
      )}
      {topNav && (
        <TopOnMap nav={topNav} crash={{ list: navClusters, idx: clusterIdx, go: goCluster }}
          onGo={(x, zoom) => setNavFocus({ id: x.id, lat: x.lat, lng: x.lng, zoom, stamp: Date.now() })} />
      )}
      {!topNav && navClusters.length > 0 && (
        <div className="so-cluster-nav" role="group" aria-label="Crash clusters">
          <button type="button" aria-label="Previous crash cluster" onClick={() => goCluster(-1)}>‹</button>
          <span>{clusterIdx < 0 ? `${navClusters.length} crash clusters` : `Crash cluster ${clusterIdx + 1} of ${navClusters.length}`}</span>
          <button type="button" aria-label="Next crash cluster" onClick={() => goCluster(1)}>›</button>
        </div>
      )}
      {far && (clusters.length > 0 || (network && network.length > 0)) && (
        <div className="ov-map-hint">
          {[clusters.length > 0 && `${clusters.length} multi-deficiency clusters`, network && network.length > 0 && `${network.length} road features`]
            .filter(Boolean).join(' · ')} · zoom in to see them
        </div>
      )}
      <Legend colourBy={runColourBy} analyses={analyses} roads={roads} details={details} showIntersections={showIntersections && intersections.length > 0} showPaths={showPaths}
        showCrashes={Boolean(crashes)} isd={isd} showNetwork={Boolean(network && network.length)}  grwShield={grwShield} />
    </div>
  );
}
